import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { authAPI, parentAPI } from '../services/api';
import getStoredUser from '../utils/getStoredUser';
import BrandLogo from './BrandLogo';

export default function ParentInviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [recipientStatus, setRecipientStatus] = useState(null);
  const [oauthProviders, setOauthProviders] = useState({ google: true, microsoft: true });

  const storedUser = getStoredUser();
  const user = Object.keys(storedUser).length ? storedUser : null;
  const isLoggedIn = !!user;
  const userRole = user?.role;

  useEffect(() => {
    let active = true;

    const fetchInviteInfo = async () => {
      try {
        setLoading(true);
        setRecipientStatus(null);
        const res = await parentAPI.getInviteInfo(token);
        const info = res.data?.data || res.data;
        if (!active) return;
        setInviteInfo(info);
        if (isLoggedIn) {
          try {
            const statusRes = await parentAPI.getInviteRecipientStatus(token);
            if (!active) return;
            setRecipientStatus(statusRes.data?.data || statusRes.data);
          } catch (statusError) {
            if (!active) return;
            if ([401, 403].includes(statusError.response?.status)) {
              setRecipientStatus({
                matchesRecipient: false,
                recipientEmail: info?.email || '',
              });
            } else {
              throw statusError;
            }
          }
        }
      } catch (err) {
        if (!active) return;
        console.error('Failed to fetch invite info:', err);
        const errMsg = err.response?.data?.error || '';
        if (errMsg.includes('already accepted') && isLoggedIn) {
          navigate(userRole === 'parent' ? '/parent' : '/parent-portal');
          return;
        }
        setError(errMsg || 'Invalid or expired invitation link.');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchInviteInfo();
    return () => { active = false; };
  }, [isLoggedIn, navigate, token, userRole]);

  useEffect(() => {
    let active = true;
    authAPI.getProviders()
      .then((res) => {
        if (!active) return;
        const data = res?.data || {};
        setOauthProviders({
          google: data.google !== false,
          microsoft: data.microsoft !== false,
        });
      })
      .catch(() => {
        if (!active) return;
        setOauthProviders({ google: true, microsoft: true });
      });
    return () => { active = false; };
  }, []);

  const handleAccept = async () => {
    try {
      setAccepting(true);
      const res = await parentAPI.acceptInvite(token);
      alert('Successfully linked to ' + (inviteInfo?.childName || 'player') + '!');

      let updatedUser = null;
      try {
        const meRes = await api.get('/auth/me');
        updatedUser = meRes.data?.data || meRes.data;
      } catch (e) {
        console.warn('Failed to refresh user via /auth/me, falling back to invite response', e);
        const data = res.data?.data || res.data || {};
        updatedUser = data.user || null;
      }

      if (updatedUser) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      window.location.href = '/parent';
    } catch (err) {
      console.error('Failed to accept invite:', err);
      alert(err.response?.data?.error || err.response?.data?.message || 'Failed to accept invitation. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const handleCreateAccount = () => {
    const params = new URLSearchParams({
      inviteToken: token,
      redirect: '/parent-invite/' + token,
    });
    if (inviteInfo?.email) params.set('invitedEmail', inviteInfo.email);
    navigate('/register?' + params.toString());
  };

  const handleSignIn = () => {
    navigate('/login?inviteToken=' + token);
  };

  const handleUseDifferentAccount = async () => {
    try {
      await authAPI.logout();
    } catch {
      // Continue locally so a stale session cannot keep showing the wrong account.
    }
    localStorage.removeItem('user');
    window.location.href = '/login?inviteToken=' + encodeURIComponent(token);
  };

  const brandHeader = (
    <div style={styles.header}>
      <div style={styles.headerInner}>
        <BrandLogo height={46} showBeta />
        <span style={styles.headerTagline}>Player development, connected.</span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        {brandHeader}
        <div style={styles.content}>
          <div style={styles.card}>
            <BrandLogo variant="icon" height={58} decorative />
            <div style={styles.loading}>Loading your secure invitation...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        {brandHeader}
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.errorIcon}>⚠️</div>
            <div style={styles.title}>Invitation Error</div>
            <div style={styles.errorText}>{error}</div>
            <button style={styles.btnSecondary} onClick={() => navigate('/login')}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {brandHeader}

      <div style={styles.content}>
        <div style={styles.card}>
          <div style={styles.brandMark}>
            <BrandLogo variant="icon" height={72} decorative />
          </div>
          <div style={styles.eyebrow}>Elite Edge Member Portal</div>
          <div style={styles.title}>Member Portal Invitation</div>
          <div style={styles.description}>
            You've been invited to join the EliteEdge AI Member Portal to follow your child's progress.
          </div>

          <div style={styles.infoBox}>
            <div style={styles.infoLabel}>Player</div>
            <div style={styles.infoValue}>{inviteInfo?.childName || 'Unknown'}</div>
          </div>

          {inviteInfo?.teamName && (
            <div style={styles.infoBox}>
              <div style={styles.infoLabel}>Team</div>
              <div style={styles.infoValue}>{inviteInfo.teamName}</div>
            </div>
          )}

          {isLoggedIn ? (
            <div style={styles.actionArea}>
              <div style={styles.loggedInText}>
                Logged in as: <strong>{user.email}</strong>
              </div>
              {recipientStatus?.matchesRecipient === false ? (
                <>
                  <div style={styles.recipientWarning} role="alert">
                    This invitation was sent to <strong>{recipientStatus.recipientEmail || inviteInfo?.email}</strong>,
                    not the account currently signed in. No player has been linked.
                  </div>
                  <button style={styles.btnPrimary} onClick={handleUseDifferentAccount}>
                    Sign out and use the invited account
                  </button>
                </>
              ) : (
                <button
                  style={styles.btnPrimary}
                  onClick={handleAccept}
                  disabled={accepting}
                >
                  {accepting ? 'Linking...' : `Link to ${inviteInfo?.childName || 'Player'}`}
                </button>
              )}
            </div>
          ) : (
            <div style={styles.actionArea}>
              <div style={styles.actionText}>
                To access the member portal, please create an account or sign in.
              </div>
              <button style={styles.btnPrimary} onClick={handleCreateAccount}>
                Create Account
              </button>
              <button style={styles.btnSecondary} onClick={handleSignIn}>
                Sign In
              </button>
              {(oauthProviders.google || oauthProviders.microsoft) && (
                <>
                  <div style={styles.divider}>
                    <span style={styles.dividerText}>or continue with</span>
                  </div>
                  {oauthProviders.google && (
                    <button style={styles.btnGoogle} onClick={() => {
                      sessionStorage.setItem('pendingParentInvite', JSON.stringify({ token, storedAt: Date.now() }));
                      localStorage.removeItem('pendingParentInvite');
                      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                      window.location.href = API_URL.replace(/\/api$/, '') + '/api/auth/google';
                    }}>
                      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: 8, verticalAlign: 'middle' }}><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                      Continue with Google
                    </button>
                  )}
                  {oauthProviders.microsoft && (
                    <button style={styles.btnMicrosoft} onClick={() => {
                      sessionStorage.setItem('pendingParentInvite', JSON.stringify({ token, storedAt: Date.now() }));
                      localStorage.removeItem('pendingParentInvite');
                      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                      window.location.href = API_URL.replace(/\/api$/, '') + '/api/auth/microsoft';
                    }}>
                      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 21 21" style={{ marginRight: 8, verticalAlign: 'middle' }}><rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/><rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>
                      Continue with Microsoft
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div style={styles.footer}>Elite Edge AI · Turn Observations Into Better Coaching.</div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
    background: 'radial-gradient(circle at top, rgba(45, 212, 191, 0.12), transparent 38%), var(--bg-input)',
    color: 'var(--text-primary)',
    minHeight: '100vh',
    paddingBottom: '2rem',
  },
  header: {
    backgroundColor: 'var(--bg-card)',
    borderBottom: '1px solid rgba(45, 212, 191, 0.22)',
    padding: '0.9rem 1.25rem',
    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.22)',
  },
  headerInner: {
    width: '100%',
    maxWidth: '1080px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
  },
  headerTagline: {
    color: 'var(--text-secondary)',
    fontSize: '0.85rem',
    letterSpacing: '0.02em',
  },
  content: {
    padding: '2rem 1rem',
    maxWidth: '600px',
    margin: '0 auto',
  },
  card: {
    backgroundColor: 'var(--bg-card)',
    border: '1px solid rgba(45, 212, 191, 0.2)',
    borderRadius: '18px',
    padding: 'clamp(1.5rem, 5vw, 2.5rem)',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.28)',
    textAlign: 'center',
  },
  loading: {
    padding: '2rem',
    fontSize: '1.1rem',
    color: 'var(--text-secondary)',
  },
  brandMark: {
    display: 'inline-flex',
    padding: '0.75rem',
    marginBottom: '1rem',
    borderRadius: '18px',
    background: 'linear-gradient(145deg, rgba(45, 212, 191, 0.16), rgba(45, 212, 191, 0.04))',
    border: '1px solid rgba(45, 212, 191, 0.22)',
  },
  eyebrow: {
    color: 'var(--accent)',
    fontSize: '0.78rem',
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    marginBottom: '0.65rem',
  },
  errorIcon: {
    fontSize: '4rem',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 700,
    marginBottom: '1rem',
    color: 'var(--text-primary)',
  },
  description: {
    fontSize: '1rem',
    color: 'var(--text-secondary)',
    marginBottom: '1.5rem',
    lineHeight: 1.6,
  },
  errorText: {
    fontSize: '1rem',
    color: '#ef4444',
    marginBottom: '1.5rem',
  },
  infoBox: {
    backgroundColor: 'var(--bg-input)',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem',
    textAlign: 'left',
  },
  infoLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '0.5rem',
  },
  infoValue: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  actionArea:{
    marginTop: '2rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  actionText: {
    fontSize: '0.95rem',
    color: 'var(--text-secondary)',
    marginBottom: '0.5rem',
  },
  loggedInText: {
    fontSize: '0.95rem',
    color: 'var(--text-secondary)',
    marginBottom: '0.5rem',
  },
  recipientWarning: {
    padding: '0.875rem',
    borderRadius: '8px',
    border: '1px solid #f59e0b',
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    color: 'var(--text-primary)',
    lineHeight: 1.5,
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, var(--accent), var(--accent-hover))',
    color: 'white',
    padding: '0.875rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
  },
  footer: {
    marginTop: '1.25rem',
    color: 'var(--text-secondary)',
    fontSize: '0.82rem',
    textAlign: 'center',
  },
  btnSecondary: {
    backgroundColor: 'transparent',
    color: 'var(--text-secondary)',
    padding: '0.875rem 1.5rem',
    border: '1px solid var(--border-input)',
    borderRadius: '8px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
  },
  divider: {
    textAlign: 'center',
    margin: '1rem 0',
    position: 'relative',
  },
  dividerText: {
    fontSize: '0.82rem',
    color: 'var(--text-secondary)',
    background: 'var(--bg-card)',
    padding: '0 0.75rem',
    position: 'relative',
    zIndex: 1,
  },
  btnGoogle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    backgroundColor: '#ffffff',
    color: '#323248',
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.95rem',
    marginBottom: '0.5rem',
    transition: 'all 0.2s ease',
  },
  btnMicrosoft: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    backgroundColor: '#2f2f2f',
    color: '#ffffff',
    padding: '0.75rem 1.5rem',
    border: '1px solid var(--border-input)',
    borderRadius: '8px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.95rem',
    transition: 'all 0.2s ease',
  },
};
